import Modal, { ModalSize } from "../../../../common/components/Modal";
import AtcProductInfo from "../../addToCart/AtcProductInfo";
import { PackagingSelection } from "../../../../common/components/PackagingSelection/PackagingSelection";
import {
  getCountriesListByProductDescriptor,
  getPriceInfo,
  isProductPMA,
} from "../../../../feature/ProductDescriptor/v1/productDescriptorUtils";
import { Permission, userHasAnyPermission, userHasPermission } from "../../../../utils/permissions";
import OptimeSolutionsMessage from "../../../../common/components/Messages/OptimeSolutionsMessage";
import DisclaimerCountryOrigin from "../../../../common/components/Messages/DisclaimerCountryOrigin";
import { useAddToCartWizardContext } from "../AddToCartWizard";
import { useFormContext } from "react-hook-form";
import { type AddToCartFormData } from "../../../hooks/useAddToCartNew";
import { selectCountryOfOriginPerCode } from "../../../../redux/products/productsSelectors";
import useMediaQuery from "../../../../common/hooks/useMediaQuery";
import { MEDIA_QUERY_MIN_DESKTOP_S } from "../../../../utils/screenSizes";
import { isSpecialPrice } from "~/ProductDetails/redux/productDetailsSelectors";
import { IncludesSurchargeDisclaimer } from "~/common/components/Messages/IncludesSurchargeDisclaimer";
import { InfoProductMessagePMA } from "~/ProductDetails/components/ProductSummary/InfoProductMessagePMA";
import { type PdpSaleData } from "~/SalesCampaign/utils";
import { useSalesData } from "~/SalesCampaign/useSalesData";
import { Alert } from "~/common/m3/Alert/Alert";
import { Stacker } from "~/common/components/Stacker/Stacker";
import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";

interface Props {
  productDescriptor: ProductDescriptor;
  submit: () => void;
  overQtyMessageEnabled?: boolean;
  sending: boolean;
}

export const AddToCartModal = ({ productDescriptor, submit, overQtyMessageEnabled = true, sending }: Props) => {
  const { t, getModalProps, getMainCls, editMode, getCloseBtn, getActionBtn, closePopup, restrictionQuery } =
    useAddToCartWizardContext();
  const { watch, setValue } = useFormContext<AddToCartFormData>();

  let okLabel;
  if (editMode) {
    okLabel = "quoteListPage.applyBtn";
  } else {
    if (isProductPMA(productDescriptor)) {
      okLabel = "cartPage.btn.quote";
    } else {
      okLabel = "addToCartPopup.add.submitBtn";
    }
  }
  okLabel = t(okLabel);

  const quantity = watch("quantity");
  const priceReference = watch("priceReference");
  const price = watch("netPrice");
  const priceUnit = watch("priceUnit");
  const hasSurcharge = watch("surcharge") > 0;

  const tariffSurcharge = watch("tariffSurcharge");

  const selectedPack = watch("selectedPack");
  const selectedPackCode = watch("selectedPackCode");

  const onSelectPackage = (packCode: string) => setValue("selectedPackCode", packCode);

  const isSpecial = isSpecialPrice(productDescriptor, selectedPackCode);

  const countryOfOriginPerCode = selectCountryOfOriginPerCode(productDescriptor.prices);

  const priceInfo = getPriceInfo({ product: productDescriptor, packCode: selectedPackCode });

  const showBetterDeal =
    overQtyMessageEnabled &&
    priceInfo &&
    priceInfo.minQtyAvailableScale !== null &&
    watch("totalQuantity") >= priceInfo.minQtyAvailableScale;

  const isPackageSizeInvalid = quantity === 0 || (selectedPack && !Number.isInteger(quantity / selectedPack.packSize));

  const addToCartDisabled = isPackageSizeInvalid || !selectedPack;
  const isSubmitEnabled = !addToCartDisabled;

  const showAddToCart = userHasAnyPermission(Permission.ADD_TO_CART, Permission.ADD_TO_QUOTE);
  const isDesktop = useMediaQuery(MEDIA_QUERY_MIN_DESKTOP_S);

  const salesData = useSalesData();
  const saleForPack: PdpSaleData | undefined = salesData?.salePerPack[selectedPackCode];
  const saleActive = !!saleForPack && watch("salePriceEnabled");
  const exceedsSalesStock = !!saleForPack && quantity > saleForPack.salesStock;

  const renderSalesInfoMessage =
    saleActive && exceedsSalesStock
      ? (packCode: string) => {
          if (packCode === selectedPackCode) {
            return <Alert severity={"info"}>{t("feature.salesCampaign.exceedsQty")}</Alert>;
          }
          return null;
        }
      : undefined;

  return (
    <Modal
      size={ModalSize.MEDIUM}
      onClose={closePopup}
      footerChildren={
        <Stacker direction={"row"} gap={8}>
          {getCloseBtn(t("cancel"))}
          {showAddToCart && getActionBtn(okLabel, submit, !isSubmitEnabled || sending, "add-to-cart")}
        </Stacker>
      }
      {...getModalProps()}
    >
      <div className="scha__atc-popup__wrapper">
        <div className={getMainCls()}>
          <div className="scha__atc-popup--column-info">
            <AtcProductInfo
              quantity={quantity}
              price={price}
              onChange={(newQty) => setValue("quantity", newQty, { shouldDirty: true, shouldTouch: true })}
              invalidQuantity={isPackageSizeInvalid ?? undefined}
              priceUnit={priceUnit}
              priceReference={priceReference}
              onPriceReferenceChange={(newVal) => setValue("priceReference", newVal ?? "")}
              descriptor={productDescriptor}
              hasSurcharge={hasSurcharge}
              isSpecialPrice={isSpecial}
              showInfoOrderHandlingUnitQty={true}
              disabled={sending}
            />
          </div>
          <div className="scha__atc-popup--column-packaging__wrapper scha__atc-popup--column-packaging">
            <div className="scha__atc-popup--column-packaging">
              {isPackageSizeInvalid && (
                <Alert className="mb-3" severity="error">
                  {t("addToCartPopup.mismatchedQty")}
                </Alert>
              )}
              {showBetterDeal && (
                <Alert severity="info" className={isDesktop ? "mb-3" : ""}>
                  {t("addProduct.scalingDeal")}
                </Alert>
              )}
              {isDesktop && (
                <div className="scha__atc-packaging-opts">
                  <div className="scha__atc-packaging-opts__title">{t("addToCartPopup.packagingOption")}</div>
                  <PackagingSelection
                    selectedCode={selectedPackCode}
                    onSelectCode={onSelectPackage}
                    product={productDescriptor}
                    renderExtraInfoOnItem={renderSalesInfoMessage}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        {!isDesktop && (
          <div className="scha__atc-packaging-opts">
            <div className="scha__atc-packaging-opts__title">{t("addToCartPopup.packagingOption")}</div>
            <div className="scha__atc-packaging-opts-wrapper">
              <PackagingSelection
                selectedCode={selectedPackCode}
                onSelectCode={onSelectPackage}
                product={productDescriptor}
                disabled={sending}
              />
            </div>
          </div>
        )}
        <Stacker gap={8} className="notifications">
          {isProductPMA(productDescriptor) && <InfoProductMessagePMA />}
          {userHasPermission(Permission.ADD_TO_CART) && <Alert severity="info">{t("addToCartPopup.disclaimer")}</Alert>}
          {productDescriptor.base.isOptime && <OptimeSolutionsMessage />}
          {getCountriesListByProductDescriptor({ descriptor: productDescriptor }).length > 0 && (
            <DisclaimerCountryOrigin />
          )}
        </Stacker>
        {
          <IncludesSurchargeDisclaimer
            hasSurcharge={hasSurcharge}
            tariffSurcharge={tariffSurcharge}
            className={"mt-30"}
          />
        }
      </div>
    </Modal>
  );
};
